import type {
  ConvertQuotationToInvoiceInput,
  CreateDocumentDTO,
  CreateMyDocumentPaymentInput,
  DocumentDetailResult,
  DocumentsListResult,
  MyDocumentsSummary,
  QuotationInvoiceCustomerListItem,
  QuotationInvoiceDocumentTypeListItem,
} from "@/modules/quotation-invoice/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const BASE = `${config.apiUrl}/quotation-invoice/my`;

export type CustomersLookupResult = {
  items: QuotationInvoiceCustomerListItem[];
  total: number;
};

export type DocumentTypesLookupResult = {
  items: QuotationInvoiceDocumentTypeListItem[];
  total: number;
};

export type CreateCustomerInput = {
  name: string;
  address?: string | null;
  contact?: string | null;
  taxId?: string | null;
};

/** API ສຳລັບ Employee Portal — ສະເພາະເອກະສານຂອງຜູ້ໃຊ້ */
export const myDocumentsApi = {
  list(query: Partial<OffsetPageQueryDTO> = {}): Promise<DocumentsListResult> {
    const params = new URLSearchParams();
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));
    return fetcher.get<DocumentsListResult>(`${BASE}?${params.toString()}`);
  },

  summary(): Promise<MyDocumentsSummary> {
    return fetcher.get<MyDocumentsSummary>(`${BASE}/summary`);
  },

  get(id: string) {
    return fetcher.get(`${BASE}/${id}`);
  },

  getDetail(id: string): Promise<DocumentDetailResult> {
    return fetcher.get<DocumentDetailResult>(`${BASE}/${id}/detail`);
  },

  create(input: CreateDocumentDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(BASE, input);
  },

  update(id: string, input: CreateDocumentDTO): Promise<{ id: string }> {
    return fetcher.put<{ id: string }>(`${BASE}/${id}`, input);
  },

  delete(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${BASE}/${id}`);
  },

  updateStatus(
    id: string,
    status: "issued" | "cancelled",
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${BASE}/${id}/status`, { status });
  },

  checkIssueStock(id: string): Promise<{
    ok: boolean;
    shortfalls: import("./catalog-types").QiStockShortfallDTO[];
  }> {
    return fetcher.get(`${BASE}/${id}/issue-stock-check`);
  },

  convertToInvoice(
    id: string,
    body?: ConvertQuotationToInvoiceInput,
  ): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${BASE}/${id}/convert-to-invoice`,
      body ?? {},
    );
  },

  convertToSalesOrder(id: string): Promise<{
    salesOrderId: string;
    orderNo: string;
  }> {
    return (async () => {
      const res = await fetch(`${BASE}/${id}/convert-to-sales-order`, {
        method: "POST",
        credentials: "include",
      });
      const body = (await res.json().catch(() => ({}))) as {
        salesOrderId?: string;
        orderNo?: string;
        message?: string;
        error?: string;
        invalidLines?: Array<{
          orderIndex: number;
          description: string;
          reason: string;
        }>;
      };
      if (!res.ok) {
        const err = new Error(
          body.message ?? body.error ?? `convert-to-sales-order: ${res.statusText}`,
        ) as Error & {
          code?: string;
          invalidLines?: typeof body.invalidLines;
        };
        err.code = body.error;
        err.invalidLines = body.invalidLines;
        throw err;
      }
      if (!body.salesOrderId || !body.orderNo) {
        throw new Error("Invalid response from convert-to-sales-order");
      }
      return { salesOrderId: body.salesOrderId, orderNo: body.orderNo };
    })();
  },

  convertToReceipt(id: string, paymentId: string): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${BASE}/${id}/convert-to-receipt`, { paymentId });
  },

  getNextDocumentNumber(params: {
    documentType: string;
    year?: number;
    excludeDocumentId?: string;
  }): Promise<{ documentNumber: string }> {
    const search = new URLSearchParams();
    search.set("documentType", params.documentType);
    if (params.year != null) search.set("year", String(params.year));
    if (params.excludeDocumentId)
      search.set("excludeDocumentId", params.excludeDocumentId);
    return fetcher.get<{ documentNumber: string }>(
      `${BASE}/next-document-number?${search.toString()}`,
    );
  },

  getCustomers(
    params: {
      q?: string;
      limit?: number;
      skip?: number;
      includeInactive?: boolean;
    } = {},
  ): Promise<CustomersLookupResult> {
    const search = new URLSearchParams();
    if (params.q != null) search.set("q", params.q);
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.skip != null) search.set("skip", String(params.skip));
    if (params.includeInactive === true) search.set("includeInactive", "true");
    return fetcher.get<CustomersLookupResult>(
      `${BASE}/customers?${search.toString()}`,
    );
  },

  createCustomer(
    input: CreateCustomerInput,
  ): Promise<QuotationInvoiceCustomerListItem> {
    return fetcher.post<QuotationInvoiceCustomerListItem>(
      `${BASE}/customers`,
      input,
    );
  },

  async getCustomer(
    id: string,
  ): Promise<QuotationInvoiceCustomerListItem | null> {
    const res = await fetch(`${BASE}/customers/${id}`, {
      credentials: "include",
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Failed to fetch customer: ${res.statusText}`);
    return res.json() as Promise<QuotationInvoiceCustomerListItem>;
  },

  getDocumentTypes(
    params: { limit?: number; includeInactive?: boolean } = {},
  ): Promise<DocumentTypesLookupResult> {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.includeInactive === true) search.set("includeInactive", "true");
    return fetcher.get<DocumentTypesLookupResult>(
      `${BASE}/document-types?${search.toString()}`,
    );
  },

  createDocumentPayment(
    documentId: string,
    input: CreateMyDocumentPaymentInput,
  ): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${BASE}/${documentId}/payments`,
      input,
    );
  },
};
